import { useEffect, useMemo, useState } from "react";
import { toSelectionChip } from "../mappers";
import { buildSearchResult, filterServerTools } from "../search";
import {
  isNewSearchSession,
  pickAutoSearchScope,
  resolveSearchScope,
} from "../search-scope";
import {
  applyRemoveTools,
  applyToggleServer,
  applyToggleTool,
  applyToggleVisibleTools,
  deriveSelectedCatalogState,
  fqnsForServer,
} from "../selection";
import {
  isUnoptimizedServer,
  type McpSearchScope,
  type McpServer,
  type Mode,
} from "../types";
import { deriveVisibleSelectionState, type PickerView } from "../view-model";
import { useSelectionSync } from "./use-selection-sync";

type UseMcpServerPickerProps = {
  servers: McpServer[];
  selectedFqns?: string[];
  defaultSelectedFqns?: string[];
  mode: Mode;
  onChange?: (selectedFqns: string[]) => void;
};

export function useMcpServerPicker({
  servers,
  selectedFqns,
  defaultSelectedFqns,
  mode,
  onChange,
}: UseMcpServerPickerProps) {
  const { selected, commitSelection } = useSelectionSync({
    servers,
    selectedFqns,
    defaultSelectedFqns,
    onChange,
  });

  const [query, setQueryState] = useState("");
  const [pinnedScope, setPinnedScope] = useState<McpSearchScope | null>(null);
  const [view, setView] = useState<PickerView>({ type: "servers" });
  const [showUnoptimizedMatches, setShowUnoptimizedMatches] = useState(false);
  // Drill-in tools filter, separate from the catalog `query`: it narrows the
  // open server's own tool list and resets on each drill-in, so it never reads
  // as a leftover catalog filter.
  const [toolQuery, setToolQuery] = useState("");

  const serverByName = useMemo(
    () => new Map(servers.map((s) => [s.name, s])),
    [servers]
  );

  // Everything derives from the single live query so the input box, the
  // hasSearch flag, the filtered results, the scope tabs, and "Select all"
  // (which acts on the current visible set) can never disagree. Deferring the
  // derivation would make the box show text while the grid still reflects the
  // previous query, and Select-all would then act on the pre-typing catalog.
  // The catalog is small enough (Arcade's toolkits are in the dozens; buildSearchResult
  // caches match haystacks by object identity) that a synchronous per-keystroke
  // filter is cheap; the potentially large path — a single server's tool list
  // filtered by `toolQuery` — is already keyed on its own live state.
  const hasSearch = query.trim() !== "";

  const {
    filteredServers,
    filteredToolGroups,
    filteredToolCount,
    unoptimizedMatch,
  } = useMemo(
    () =>
      buildSearchResult({
        query,
        servers,
        showUnoptimizedMatches,
      }),
    [query, servers, showUnoptimizedMatches]
  );

  const autoScope = useMemo(
    () =>
      pickAutoSearchScope({
        serverCount: filteredServers.length,
        toolCount: filteredToolCount,
        unoptimizedServerCount: unoptimizedMatch.serverCount,
        unoptimizedToolCount: unoptimizedMatch.toolCount,
      }),
    [
      filteredServers.length,
      filteredToolCount,
      unoptimizedMatch.serverCount,
      unoptimizedMatch.toolCount,
    ]
  );

  const searchScope = resolveSearchScope({ hasSearch, pinnedScope, autoScope });

  const selectedCatalog = useMemo(
    () => deriveSelectedCatalogState(servers, selected),
    [servers, selected]
  );

  // Chips for the selection summary bar: one per server with a selection, in
  // catalog order. Each group's `tools` is already the selected subset, so its
  // length is the server's selected count.
  const summaryChips = useMemo(
    () =>
      selectedCatalog.groups.map(({ server, tools }) =>
        toSelectionChip(server, tools.length)
      ),
    [selectedCatalog.groups]
  );

  const hasUnoptimizedServers = useMemo(
    () => servers.some(isUnoptimizedServer),
    [servers]
  );

  // The open server (drill-in view) and its tools narrowed by the tools-view
  // search. Derived once here so the same filtered list drives both the tools
  // view's rendering and the "Select all visible" math — they can't disagree.
  const drillInServer =
    view.type === "tools" ? serverByName.get(view.serverName) : undefined;
  const drillInTools = useMemo(
    () => (drillInServer ? filterServerTools(drillInServer, toolQuery) : []),
    [drillInServer, toolQuery]
  );

  // The review view lists each selected server's *full* tool set (not just the
  // selected tools) so the user can toggle the rest without leaving the screen.
  const reviewGroups = useMemo(
    () =>
      selectedCatalog.groups.map(({ server }) => ({
        server,
        tools: server.tools,
      })),
    [selectedCatalog.groups]
  );

  const visibleSelection = useMemo(
    () =>
      deriveVisibleSelectionState({
        drillInTools,
        filteredServers,
        filteredToolGroups,
        hasSearch,
        searchScope,
        selected,
        view,
      }),
    [
      drillInTools,
      filteredServers,
      filteredToolGroups,
      hasSearch,
      searchScope,
      selected,
      view,
    ]
  );

  const fqnsForServerName = (serverName: string): string[] =>
    fqnsForServer(serverByName.get(serverName));

  const toggleTool = (fqn: string) =>
    commitSelection(applyToggleTool(selected, fqn, mode));

  const toggleServer = (serverName: string) => {
    if (mode === "single") {
      return;
    }
    commitSelection(applyToggleServer(selected, fqnsForServerName(serverName)));
  };

  const removeServer = (serverName: string) =>
    commitSelection(applyRemoveTools(selected, fqnsForServerName(serverName)));

  const clearSelection = () => commitSelection(new Set());

  const toggleSelectAllVisible = () => {
    if (mode === "single") {
      return;
    }
    commitSelection(
      applyToggleVisibleTools({
        selected,
        visibleToolFqns: visibleSelection.visibleToolFqns,
        allVisibleSelected: visibleSelection.allVisibleSelected,
        servers,
        // Hidden unoptimized tools are only swept on a "clear all" from the
        // unfiltered server grid — that's the only screen where the user sees
        // (and believes they're clearing) the whole catalog.
        sweepHiddenUnoptimized:
          !(showUnoptimizedMatches || hasSearch) && view.type === "servers",
      })
    );
  };

  const setQuery = (next: string) => {
    if (isNewSearchSession(query, next)) {
      setPinnedScope(null);
    }
    setQueryState(next);
    if (next.trim() && view.type !== "servers") {
      setView({ type: "servers" });
    }
  };

  const setSearchScope = (scope: McpSearchScope) => {
    const effective = pinnedScope ?? autoScope;
    if (scope === effective) {
      return;
    }
    setPinnedScope(scope);
  };

  // Drilling into a server is its own browsing context with its own search
  // (`toolQuery`), which filters that server's tools. The catalog `query` is
  // kept — not cleared — so `backToServers` restores the user's filtered catalog
  // instead of dropping them at the top; it never filters the tools list. The
  // two searches are deliberately separate: `toolQuery` resets on each drill-in
  // (below) so a stale catalog query never leaks in as an unapplied tools filter.
  const openTools = (serverName: string) => {
    if (!serverByName.has(serverName)) {
      return;
    }
    setToolQuery("");
    setView({ type: "tools", serverName });
  };

  const reviewSelection = () => setView({ type: "review" });
  const backToServers = () => setView({ type: "servers" });

  useEffect(() => {
    if (view.type === "tools" && !serverByName.has(view.serverName)) {
      setView({ type: "servers" });
    }
  }, [view, serverByName]);

  return {
    state: {
      // Optimistic UI selection: render this as the checked set. It can briefly
      // include a pending default whose server is still loading, and those FQNs
      // are withheld from `onChange` until the catalog confirms them. Persist
      // from `onChange` (only ever real, in-catalog tools), never from this set.
      selected,
      selectedGroups: selectedCatalog.groups,
      selectedToolCount: selectedCatalog.toolCount,
      summaryChips,
      hasUnoptimizedServers,
      drillInServer,
      drillInTools,
      reviewGroups,
      query,
      toolQuery,
      hasSearch,
      searchScope,
      view,
      filteredServers,
      filteredToolGroups,
      filteredToolCount,
      showUnoptimizedMatches,
      unoptimizedMatch,
      visibleCount: visibleSelection.visibleToolFqns.length,
      allVisibleSelected: visibleSelection.allVisibleSelected,
      someVisibleSelected: visibleSelection.someVisibleSelected,
    },
    actions: {
      setQuery,
      setToolQuery,
      setSearchScope,
      setShowUnoptimizedMatches,
      toggleTool,
      toggleServer,
      removeServer,
      clearSelection,
      toggleSelectAllVisible,
      openTools,
      reviewSelection,
      backToServers,
    },
  };
}

type McpServerPickerApi = ReturnType<typeof useMcpServerPicker>;
/** Reactive picker state exposed by {@link useMcpServerPicker}. */
export type McpServerPickerState = McpServerPickerApi["state"];
/** Picker action handlers exposed by {@link useMcpServerPicker}. */
export type McpServerPickerActions = McpServerPickerApi["actions"];
